import { Box } from "@chakra-ui/react";

import type { Media } from "api/schemas";
import { useIsEmbedded } from "utils";
import { buildImageUrl, buildVideoVariants } from "utils/buildUrl";
import { constructImageUrl } from "utils/constructImageUrl";

export const CoverMedia = ({
  leadMedia,
  leadVideo,
}: {
  leadMedia?: Media | null;
  leadVideo?: Media | null;
}) => {
  const { height } = useIsEmbedded();
  const videoVariants = buildVideoVariants(leadVideo?.data);

  const src480 = constructImageUrl(leadMedia, { width: 480 });
  const src720 = constructImageUrl(leadMedia, { width: 720 });
  const src1080 = constructImageUrl(leadMedia, { width: 1080 });
  const coverImageUrl =
    src720 ?? buildImageUrl(leadMedia?.data, "/search/buildings.avif");
  const srcSet =
    src480 && src720 && src1080
      ? `${src480} 480w, ${src720} 720w, ${src1080} 1080w`
      : undefined;

  // Browser picks the first <source> it can decode — order matters.
  // `media` on <source> inside <video> is ignored by all browsers (only valid
  // in <picture>), so we order by quality: webm first (better compression),
  // then mp4 fallback. The raw original is the last-resort fallback.
  const videoWebm = videoVariants.find((v) => v.type === "video/webm");
  const videoMp4 = videoVariants.find(
    (v) => v.src.includes("720_") && v.type === "video/mp4"
  );
  const videoFallback = videoVariants.at(-1);

  return (
    <Box
      width={{ base: "100dvw", md: "50dvw" }}
      height={height}
      flexShrink={0}
      overflow="hidden"
      position="relative"
    >
      {videoVariants.length > 0 ? (
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        >
          {videoWebm && <source src={videoWebm.src} type={videoWebm.type} />}
          {videoMp4 && <source src={videoMp4.src} type={videoMp4.type} />}
          {videoFallback && (
            <source src={videoFallback.src} type={videoFallback.type} />
          )}
        </video>
      ) : (
        <img
          src={coverImageUrl ?? undefined}
          srcSet={srcSet}
          // Accounts for device pixel ratio: a 430px iPhone 14 Pro Max at 3x
          // DPR would request a 1290px image — cap at 480w for mobile half-pane.
          sizes="(max-width: 768px) min(100vw, 480px), 50vw"
          alt=""
          // Lowercase `fetchpriority` passes through verbatim (matches SSR),
          // avoiding the "React does not recognize fetchPriority" warning.
          {...({
            fetchpriority: "high",
          } as React.ImgHTMLAttributes<HTMLImageElement>)}
          loading="eager"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            backgroundColor: "var(--color-stone-300)",
          }}
        />
      )}
    </Box>
  );
};
